import React, {useEffect, useState, useRef} from 'react';
import './App.css';
import {BrowserRouter as Router, Route, Routes} from 'react-router-dom';
import {HelmetProvider} from 'react-helmet-async';
import {LanguageContext} from './LanguageContext';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RoutesHandler from "./RoutesHandler";
import Seo from "./components/common/Seo";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "./store";
import {setProfileData, setSearchVisibility} from "./components/profile/profileSlice";
import {fetchCompositeProfile} from "./hooks/usePreferences";
import {useAuth0} from '@auth0/auth0-react';
import ConsentBanner from './analytics/ConsentBanner';
import AnalyticsRouteListener from './analytics/AnalyticsRouteListener';
import {loadDeferredGtm} from './analytics/analytics';
import {flushQueuedEvents} from './analytics/events';

import {
    setError,
    setGetAccessTokenSilently, setGetIdTokenClaims,
    setIsAuthenticated,
    setIsLoading, setLoginWithRedirect,
    setLogout,
    setUser
} from "./components/navigation/login/authSlice";
import Header from "./components/navigation/Header";
import {reloadApiConfig} from "./components/api/api";
import {reloadWsApi} from "./components/api/wsapi";
import {getProfile} from "./components/profile/get-profile";
import {ColorPresetType} from "./components/pages/therapy-session/therapy-configuration";

function App() {
    const [lang, setLang] = useState('pl'); // default language
    // const [profileData, setProfileData] = useState()
    let {isLoading, error, isAuthenticated, user, logout, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect} =
        useAuth0();

    const profileData = useSelector((state: RootState) => state.profile);
    const therapyConfiguration = useSelector((state: RootState) => state.therapyConfiguration);
    const dispatch = useDispatch();

    const loginFunction = (config:any = null, event:any = null) => {
        if(event) {
            event.preventDefault(); // This stops the browser from navigating to the link.
            event.stopPropagation();
        }
        if(config) {
            return loginWithRedirect(config);
        } else {
            return loginWithRedirect();
        }
    }
    const logoutFunction = (event:any) => {
        if(event) {
            event.preventDefault(); // This stops the browser from navigating to the link.
            event.stopPropagation();
        }
        localStorage.removeItem('tok');
        localStorage.removeItem('uid');
        return logout();
    }

/*
    dispatch(setIsLoading(isLoading));
    dispatch(setError(error));
    dispatch(setIsAuthenticated(isAuthenticated));
    dispatch(setUser(user));
    dispatch(setLogout(logout));
    dispatch(setGetAccessTokenSilently(getAccessTokenSilently));
    dispatch(setGetIdTokenClaims(getIdTokenClaims));
    dispatch(setLoginWithRedirect(loginWithRedirect));

    const authData = useSelector((state: RootState) => state.auth);
*/

    let addClassName = "";
    if (document.location.href.includes("csession")) {
        addClassName = "csession";
    }

    /*  const setPageMetaData = (metadata:PageMetaData) => {
        if(metadata.title) {
          setPageTitle(metadata.title + " - emdrlink.pl");
        } else {
          setPageTitle("emdrlink.pl");
        }
      }*/
/*    useEffect(() => {
        // i18n.changeLanguage('pl');
        setTimeout(() => {
            dispatch(setProfileData(
                {
                    _id: "123123",
                    externalId: "WP123",
                    name: "Alex",
                    email: "alex@emdrlink.pl",
                    role: "therapist"
                },
            ))
        }, 500);
    }, []);*/

    // index.html defers GTM on the Auth0 callback URL (?code=&state=) because
    // the SDK rewrites that URL a moment later, which GA4 would count as a
    // second page_view. Load it once the SDK is done — unless the effect below
    // is about to reload into redirect_after_login (that page loads GTM from
    // index.html). Keep this effect ABOVE that one: it reads the flag first.
    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (isAuthenticated && localStorage.getItem("redirect_after_login")) {
            return;
        }
        flushQueuedEvents();
        loadDeferredGtm();
    }, [isLoading, isAuthenticated]);

    useEffect(() => {
        if(isAuthenticated) {
            // console.log('AUTHENTICATED!!');
            if(localStorage.getItem("redirect_after_login")) {
                const redirectPath = localStorage.getItem("redirect_after_login");
                localStorage.removeItem("redirect_after_login");
                if(redirectPath) {
                    window.location.href = redirectPath;
                }
                //will be redirect
                // return <div className={"background-image main-container"}></div>;
            }
            getAccessTokenSilently().then(
                async function (value) { /* code if successful */
                    sessionStorage.removeItem('auth_interactive_retry');
                    localStorage.setItem("tok", value);
                    reloadApiConfig(value);
                    reloadWsApi(value);
                    let theProfile:any;
                    // Hydrate the WP-owned search-listing status into redux so every
                    // screen (dashboard + settings) reads one consistent value. Kicked
                    // off in parallel with getProfile (token is already configured
                    // above) to minimise the delay before the status appears.
                    const compositePromise = fetchCompositeProfile()
                        .then((composite:any) => {
                            // A successful composite with no (or a non-published)
                            // listing => directory is null => NOT visible. Always
                            // dispatch a definite boolean so stale values can't linger.
                            dispatch(setSearchVisibility(composite?.directory?.searchVisible === true));
                        })
                        .catch(() => { /* WP unreachable (network): keep the existing value */ });
                    theProfile = await getProfile();
                    // if (!theProfile) {
                        dispatch(setProfileData(theProfile));
                    // }
                    //setTimeout(async ()=>{ let anotherProfile = await getProfile(); console.log(anotherProfile);},10000);
                    if (theProfile && theProfile._id) {
                        localStorage.setItem('uid', theProfile._id);
                    }
                    await compositePromise;
                    // setIsPageLoading(false);
                },
                function (error:any) { /* code if some error */
                    const code = error?.error ?? '';
                    if (code === 'consent_required' || code === 'login_required') {
                        // Silent token acquisition needs interactive consent/login.
                        // Recover via redirect once; guard against redirect loops.
                        if (!sessionStorage.getItem('auth_interactive_retry')) {
                            sessionStorage.setItem('auth_interactive_retry', '1');
                            loginWithRedirect();
                            return;
                        }
                        console.error('Auth0 still requires interactive consent after retry:', error);
                    } else {
                        console.error('Failed to acquire access token silently:', error);
                    }
                }
            );
        }
    },[isAuthenticated]);
    return (
        <Router>
          <AnalyticsRouteListener />
          <HelmetProvider>
            <LanguageContext.Provider value={{lang, setLang}}>
                <Seo />
                <div className={"main-body" + " " + addClassName + (therapyConfiguration.colorPreset === ColorPresetType.dark ? " dark-interaction-part" : " light-interaction-part")}>
                    {/* data-cs-mask: toasts can contain patient names (Contentsquare replay). */}
                    <div data-cs-mask="">
                        <ToastContainer/>
                    </div>
                    <Header isAuthenticated={isAuthenticated} loginFunction={loginFunction} logout={logoutFunction} />
                    <Routes>
                        <Route path="/" element={<RoutesHandler isAuthenticated={isAuthenticated} loginFunction={loginFunction} logout={logoutFunction} isLoading={isLoading} /> }/>
                        <Route path="/:langId" element={<RoutesHandler isAuthenticated={isAuthenticated} loginFunction={loginFunction} logout={logoutFunction} isLoading={isLoading} />}/>
                        <Route path="/:langId/:pageUrlPart" element={<RoutesHandler isAuthenticated={isAuthenticated} loginFunction={loginFunction} logout={logoutFunction} isLoading={isLoading} />}/>
                    </Routes>
                </div>
                <ConsentBanner />
            </LanguageContext.Provider>
          </HelmetProvider>
        </Router>
    );
}

export default App;
